import React, { Fragment, useEffect, useRef, useState } from "react";
import Validation from "Common/Validation/Validation";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import { Field } from "react-final-form";
import T from "Translate";
import { Grid, Icon } from "@material-ui/core";
import styles from "./passwordField.style";
import { withStyles } from "@material-ui/styles";
import classNames from "classnames";
import TextFieldNoErrors from "./TextFieldNoErrors";

const { required, minLength, composeValidators, hasLowerCase, hasNumber, hasSpecialChar, hasUpperCase, validPassword } =
    Validation;

const inputState = {
    UNTOUCHED: "untouched",
    VALID: "valid",
    INVALID: "invalid"
};

function boolToState(bool) {
    switch (bool) {
        case null:
            return inputState.UNTOUCHED;
        case true:
            return inputState.VALID;
        case false:
            return inputState.INVALID;
        default:
            return inputState.INVALID;
    }
}

function PasswordValidatorVisualizer({ children, state, classes, id }) {
    return (
        <Grid item xs={12} id={id} className={classNames(classes.visualizer, classes[state])}>
            <div className={classes.wrapper}>
                <div className={classes.icon}>
                    {state === inputState.VALID ? (
                        <span className={classes.checkmark}>
                            <Icon fontSize="inherit">checkmark</Icon>
                        </span>
                    ) : (
                        <span className={classes.circle}>
                            <Icon fontSize="inherit">circle</Icon>
                        </span>
                    )}
                </div>
                {/* Class added for e2e testing */}
                <div className={`${classes.text} text`}>{children}</div>
            </div>
        </Grid>
    );
}

function PasswordField({ showPassword, setShowPassword, classes, value, submitting }) {
    const [touched, setTouched] = useState(false);
    const [isLongEnough, setIsLongEnough] = useState(null);
    const [hasOneLowerCase, setHasOneLowerCase] = useState(null);
    const [hasOneUpperCase, setHasOneUpperCase] = useState(null);
    const [hasOneNumber, setHasOneNumber] = useState(null);
    const [hasOneSpecialCharacter, setHasOneSpecialCharacter] = useState(null);
    const [blurred, setBlurred] = useState(false);

    const ref = useRef(null);
    const handleClickShowPassword = () => {
        setShowPassword(status => !status);
    };

    function validationWrapper(validator) {
        const res = value && !validator(value);

        return blurred ? res : res || null;
    }

    function validateField() {
        if (!value && submitting) {
            setIsLongEnough(false);
            setHasOneLowerCase(false);
            setHasOneUpperCase(false);
            setHasOneNumber(false);
            setHasOneSpecialCharacter(false);
            setTouched(true);
            return;
        }

        setIsLongEnough(validationWrapper(minLength(8)));
        setHasOneLowerCase(validationWrapper(hasLowerCase));
        setHasOneUpperCase(validationWrapper(hasUpperCase));
        setHasOneNumber(validationWrapper(hasNumber));
        setHasOneSpecialCharacter(validationWrapper(hasSpecialChar));
    }

    useEffect(() => {
        if (!value && !touched && !blurred) {
            return;
        }

        if ((value && !touched) || (blurred && !touched)) {
            setTouched(true);
        }

        validateField();
    }, [value, blurred]);

    useEffect(() => {
        if (submitting) validateField();
    }, [submitting]);

    const passwordValidationResult = validPassword(value);

    function handleBlur() {
        setBlurred(true);
    }

    useEffect(() => {
        if (ref.current) {
            // Workaround because onBlur is not working with final-form-material-ui's TextField component
            ref.current.querySelector("#password").addEventListener("blur", handleBlur);
        }

        return () => {
            ref.current.querySelector("#password").removeEventListener("blur", handleBlur);
        };
    }, [ref]);

    return (
        <Fragment>
            <div className={classes.labelContent} data-testid="label_password">
                <T>password</T>
            </div>
            <span ref={ref}>
                <Field
                    component={TextFieldNoErrors}
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton aria-label="Toggle password visibility" onClick={handleClickShowPassword}>
                                    {showPassword ? <Visibility /> : <VisibilityOff />}
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                    validate={composeValidators(
                        required,
                        validPassword,
                        minLength(8),
                        hasLowerCase,
                        hasUpperCase,
                        hasNumber,
                        hasSpecialChar
                    )}
                    validateFields={[]}
                    fullWidth
                    className={classes.input}
                    dataTestId="password"
                />
            </span>
            {passwordValidationResult && (
                <div className={classes.invalidPassowrd} data-testid="invalid_password">
                    {passwordValidationResult}
                </div>
            )}
            <div className={classes.criteriaContainer}>
                <div className={classes.label}>
                    <T>password_must</T>
                </div>
                <Grid container>
                    <PasswordValidatorVisualizer
                        classes={classes}
                        state={boolToState(isLongEnough)}
                        id="password_must_be_at_least_8_characters"
                    >
                        <T>password_must_be_at_least_8_characters</T>
                    </PasswordValidatorVisualizer>
                    <PasswordValidatorVisualizer
                        classes={classes}
                        state={boolToState(hasOneLowerCase)}
                        id="password_must_have_at_least_one_lower_case"
                    >
                        <T>password_must_have_at_least_one_lower_case</T>
                    </PasswordValidatorVisualizer>
                    <PasswordValidatorVisualizer
                        classes={classes}
                        state={boolToState(hasOneUpperCase)}
                        id="password_must_have_at_least_one_upper_case"
                    >
                        <T>password_must_have_at_least_one_upper_case</T>
                    </PasswordValidatorVisualizer>
                    <PasswordValidatorVisualizer
                        classes={classes}
                        state={boolToState(hasOneNumber)}
                        id="password_must_have_at_least_one_number"
                    >
                        <T>password_must_have_at_least_one_number</T>
                    </PasswordValidatorVisualizer>
                    <PasswordValidatorVisualizer
                        classes={classes}
                        state={boolToState(hasOneSpecialCharacter)}
                        id="password_must_have_at_least_one_special_character"
                    >
                        <T>password_must_have_at_least_one_special_character</T>
                    </PasswordValidatorVisualizer>
                </Grid>
            </div>
        </Fragment>
    );
}

export default withStyles(styles)(PasswordField);



// WEBPACK FOOTER //
// ./src/components/StartContainer/components/PasswordField.js